import { apiDelete, ApiError, apiGet, apiPost, apiPostJSON, apiPut } from 'App/Dataman';
import { ASSISTANT_PATHS } from '@/app/api/assistant/paths';

// The assistant keeps working with our JWT after the response: it processes the URLs, creates the
// product and writes it back to WMS. A token that is still formally valid at the hand-off moment
// can expire mid-job, which surfaces in the log as
// `Error updating product in WMS: {"code":401,"message":"Expired JWT Token"}`.
// The value covers the documented 15 minutes of processing plus the wait in the queue.
// Applied to the calls that start a job only — plain GETs keep the default behaviour.
const ASSISTANT_MIN_TOKEN_TTL = 20 * 60;

/** @typedef {Object} LogItem
 * @prop {number} id
 * @prop {String} url
 * @prop {String} createdAt
 * @prop {null|boolean} success
 * @prop {String} error
 * @prop {number} wimsId
 */

/** @typedef {Object} RunAssistantResults
 * @prop {Array<String>} questions
 * @prop {Array<String>} answers
 * @prop {String} product
 */

/**
 * List of the 100 latest URLs and their status to analyse information
 * @returns {Promise<Array<LogItem>>}
 */
export function getAssistantLog(params = {}) {
  return apiGet(ASSISTANT_PATHS.assistant, { params }).then(onResponse);
}

/**
 * Provide a list of URLs to process them thorough this service
 * @param urlList {Array<string>}
 * @returns {Promise<boolean>}
 * @throws ApiError
 */
export function setAssistantUrls(urlList, params = {}) {
  return apiPostJSON(ASSISTANT_PATHS.assistant, urlList, { ...params, minTokenTtl: ASSISTANT_MIN_TOKEN_TTL });
}

/**
 * Run Assistant
 * @returns {Promise<RunAssistantResults>}
 * @throws ApiError
 */
export function runAssistant() {
  return apiPost(ASSISTANT_PATHS.runAssistant).then(onResponse);
}

/**
 * Deletes an log item
 * @param id
 * @returns {Promise<boolean>}
 */
export function deleteItem(id) {
  return apiDelete(`${ASSISTANT_PATHS.assistant}/${id}`).then((res) => {
    if (res.status === 204) {
      return true;
    } else {
      throw new ApiError(res.statusText, res);
    }
  });
}

export function reloadLogItem(id) {
  return apiPut(`${ASSISTANT_PATHS.assistant}/${id}/reload`, { minTokenTtl: ASSISTANT_MIN_TOKEN_TTL }).then((res) => {
    if (res.status === 204) {
      return true;
    } else {
      throw new ApiError(res.statusText, res);
    }
  });
}

export function editLogItem(id, data) {
  return apiPut(`${ASSISTANT_PATHS.assistant}/${id}`, { data });
}

function onResponse(res) {
  const { status, data } = res;
  if (status === 200) {
    return data;
  } else {
    throw new ApiError(res.statusText, res);
  }
}

export function getProductDifferences(wims_id) {
  return apiGet(`${ASSISTANT_PATHS.assistantDifferences}/${wims_id}${ASSISTANT_PATHS.assistantDifferenceSuffix}`).then(
    onResponse
  );
}

export function getAssistantPrompt(model_name) {
  return apiGet(ASSISTANT_PATHS.assistantPrompt, { params: { model_name } });
}

export function declineProductDifferences(wims_id) {
  return apiPut(`${ASSISTANT_PATHS.assistantDifferences}/${wims_id}${ASSISTANT_PATHS.assistantDeclineSuffix}`);
}

export function setCloneAssistant(wimsId, data, query = {}) {
  return apiPostJSON(`${ASSISTANT_PATHS.assistantClone}/${wimsId}`, data, {
    params: query,
    minTokenTtl: ASSISTANT_MIN_TOKEN_TTL,
  });
}

export function getAssistantExtractSuffix(params) {
  return apiGet(ASSISTANT_PATHS.assistantExtractSuffix, { params });
}

export function getProductInsights(wims_id) {
  return apiGet(`${ASSISTANT_PATHS.assistantDifferences}/${wims_id}${ASSISTANT_PATHS.assistantInsightsSuffix}`).then(
    onResponse
  );
}

export function getProductComment(wims_id) {
  return apiGet(`${ASSISTANT_PATHS.assistantDifferences}/${wims_id}${ASSISTANT_PATHS.assistantCommentSuffix}`).then(
    onResponse
  );
}

export default {
  getAssistantLog,
  setAssistantUrls,
  runAssistant,
  deleteItem,
  reloadLogItem,
  getProductDifferences,
  getAssistantPrompt,
  declineProductDifferences,
  editLogItem,
  setCloneAssistant,
  getAssistantExtractSuffix,
  getProductInsights,
  getProductComment,
};
